<script setup lang="ts">
definePageMeta({ localSectionNavigation: true })
const { t } = useI18n()
const title = computed(() => t('epoukazy.meta.title'))
const description = computed(() => t('epoukazy.meta.description'))
useSeoMeta({ title, description, ogTitle: title, ogDescription: description })
// The hero is white, so the strip under the transparent header must be white too
useHead({ titleTemplate: '', bodyAttrs: { class: 'bg-white' } })
defineOgImage({ component: 'Default', subtitle: computed(() => t('epoukazy.hero.eyebrow')) })
const features = computed(() =>
  [0, 1, 2].map((i) => ({
    title: t('epoukazy.features.items.' + i + '.title'),
    text: t('epoukazy.features.items.' + i + '.text'),
  }))
)
const steps = computed(() =>
  [0, 1, 2].map((i) => ({
    title: t('epoukazy.steps.items.' + i + '.title'),
    text: t('epoukazy.steps.items.' + i + '.text'),
  }))
)
const questions = computed(() =>
  [0, 1, 2, 3].map((i) => ({
    label: t('epoukazy.faq.items.' + i + '.label'),
    content: t('epoukazy.faq.items.' + i + '.content'),
  }))
)
function goToContact() {
  document.getElementById('kontakt')?.scrollIntoView()
  document.getElementById('firstName')?.focus({ preventScroll: true })
}
function goToSteps() {
  document.getElementById('jak-to-funguje')?.scrollIntoView()
  document.getElementById('jak-to-funguje')?.focus({ preventScroll: true })
}
</script>

<template>
  <main class="epoukazy">
    <section class="bg-white px-4 py-12 lg:py-20">
      <div class="container mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <p class="text-optilynx-pink mb-5 text-sm font-semibold">{{ t('epoukazy.hero.eyebrow') }}</p>
          <h1 class="mb-6 text-4xl leading-tight font-semibold text-balance lg:text-6xl">
            {{ t('epoukazy.hero.title') }}
          </h1>
          <p class="mb-8 text-lg leading-relaxed">
            {{ t('epoukazy.hero.intro') }}
          </p>
          <div class="flex flex-col gap-4 sm:flex-row">
            <UiButton type="button" @click="goToContact">{{ t('epoukazy.cta.demo') }}</UiButton>
            <UiButton type="button" dark @click="goToSteps">{{ t('epoukazy.cta.steps') }}</UiButton>
          </div>
        </div>
        <figure class="min-w-0">
          <img
            src="/img/png/epoukaz-tablet.png"
            :alt="t('epoukazy.hero.imageAlt')"
            width="1536"
            height="1024"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            class="h-auto w-full object-contain"
          />
        </figure>
      </div>
    </section>
    <UiSection id="funkce" :title="t('epoukazy.features.title')" class="bg-white">
      <p class="mx-auto mb-12 max-w-3xl text-center text-lg">
        {{ t('epoukazy.features.intro') }}
      </p>
      <div class="grid gap-10 lg:grid-cols-3">
        <article
          v-for="(feature, index) in features"
          :key="feature.title"
          class="bg-optilynx-purple min-w-0 rounded-2xl p-7 text-white sm:p-8"
        >
          <svg
            class="mb-5 h-10 w-10"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            aria-hidden="true"
          >
            <path v-if="index === 0" d="M5 11V5h6M21 5h6v6M27 21v6h-6M11 27H5v-6M10 16h12M16 10v12" />
            <path v-else-if="index === 1" d="M8 4h16v24H8zM12 10h8M12 16h8M12 22h5" />
            <path v-else d="M7 4h14l5 5v19H7zM21 4v6h5M11 18l4 4 7-8" />
          </svg>
          <h3 class="mb-4 text-2xl font-medium">{{ feature.title }}</h3>
          <p class="text-lg leading-relaxed">{{ feature.text }}</p>
        </article>
      </div>
    </UiSection>
    <UiSection :title="t('epoukazy.systems.title')" class="bg-optilynx-purple text-white">
      <p class="mx-auto mb-10 max-w-4xl text-center text-lg">
        {{ t('epoukazy.systems.intro') }}
      </p>
      <div class="grid gap-6 md:grid-cols-2">
        <article class="rounded-2xl bg-pink-50 p-7 text-black sm:p-10">
          <svg
            class="text-optilynx-purple mb-6 h-10 w-10"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            aria-hidden="true"
          >
            <rect x="4" y="5" width="24" height="17" rx="2" />
            <path d="M16 22v5M10 27h12" />
          </svg>
          <h3 class="mb-6 text-2xl font-medium">{{ t('epoukazy.systems.currentTitle') }}</h3>
          <ul class="list-disc space-y-3 pl-5 text-lg">
            <li>{{ t('epoukazy.systems.currentItems.0') }}</li>
            <li>{{ t('epoukazy.systems.currentItems.1') }}</li>
            <li>{{ t('epoukazy.systems.currentItems.2') }}</li>
            <li>{{ t('epoukazy.systems.currentItems.3') }}</li>
          </ul>
        </article>
        <article class="rounded-2xl bg-pink-50 p-7 text-black sm:p-10">
          <svg
            class="text-optilynx-purple mb-6 h-10 w-10"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            aria-hidden="true"
          >
            <path d="M8 4h13l5 5v19H8zM21 4v6h5M12 16h10M12 22h7" />
          </svg>
          <h3 class="text-optilynx-purple mb-6 text-2xl font-medium">{{ t('epoukazy.systems.optilynxTitle') }}</h3>
          <ul class="list-disc space-y-3 pl-5 text-lg">
            <li>{{ t('epoukazy.systems.optilynxItems.0') }}</li>
            <li>{{ t('epoukazy.systems.optilynxItems.1') }}</li>
            <li>{{ t('epoukazy.systems.optilynxItems.2') }}</li>
            <li>{{ t('epoukazy.systems.optilynxItems.3') }}</li>
          </ul>
        </article>
      </div>
    </UiSection>
    <UiSection id="jak-to-funguje" :title="t('epoukazy.steps.title')" tabindex="-1" class="bg-white">
      <p class="mb-12 text-center text-lg">
        {{ t('epoukazy.steps.intro') }}
      </p>
      <ol class="grid gap-10 lg:grid-cols-3">
        <li v-for="(step, index) in steps" :key="step.title" class="bg-optilynx-pink/10 rounded-2xl p-6">
          <p class="text-optilynx-pink mb-5 text-3xl font-semibold" aria-hidden="true">0{{ index + 1 }}</p>
          <h3 class="mb-4 text-2xl font-medium">{{ step.title }}</h3>
          <p class="text-lg leading-relaxed">{{ step.text }}</p>
        </li>
      </ol>
    </UiSection>
    <UiSection id="cenik" :title="t('epoukazy.pricing.title')" class="bg-white">
      <div class="bg-optilynx-purple mx-auto w-full max-w-140 rounded-2xl px-5 py-6 text-center text-white">
        <h3 class="mb-4 text-xl font-medium">{{ t('epoukazy.pricing.service') }}</h3>
        <p class="mb-5 flex flex-nowrap items-baseline justify-center gap-2 whitespace-nowrap">
          <span class="shrink-0 text-3xl font-bold sm:text-4xl">{{ t('epoukazy.pricing.amount') }}</span>
          <span class="shrink-0 text-sm sm:text-base">{{ t('epoukazy.pricing.period') }}</span>
        </p>
        <UiButton type="button" @click="goToContact">{{ t('epoukazy.cta.demo') }}</UiButton>
      </div>
    </UiSection>
    <UiSection id="faq" :title="t('epoukazy.faq.title')" class="bg-pink-50">
      <div class="bg-optilynx-purple rounded-2xl p-4"><UiAccordion :items="questions" /></div>
    </UiSection>
    <UiSection :title="t('epoukazy.closing.title')" class="bg-white">
      <p class="mx-auto mb-6 max-w-4xl text-center text-lg">
        {{ t('epoukazy.closing.intro') }}
      </p>
      <div class="text-center">
        <UiButton type="button" @click="goToContact">{{ t('epoukazy.cta.demo') }}</UiButton>
      </div>
    </UiSection>
    <HomepageContact />
  </main>
</template>

<style scoped>
.epoukazy :deep(section[id]) {
  scroll-margin-top: 6rem;
}
</style>
